/**
 * missionAnswerAdapter.js
 * 
 * Adapts mission response (Questions and Solves) from the API into 
 * the exact standardized Exam/LessonResult structure expected by QuestionView,
 * QuestionSummary, and useLessonResult without modifying any question components.
 */

export function adaptMissionData(questions = [], solves = []) {
    const qs = Array.isArray(questions) ? questions : [];
    const sls = Array.isArray(solves) ? solves : [];

    const adaptSingleAnswers = (rawAnswers, question, solve) => {
        if (!question) return [];

        const typeId = Number(question?.Type?.Id ?? question?.Type ?? question?.TypeId ?? 0);
        const qFills = Array.isArray(question?.Fills) ? question.Fills : [];
        const qCats = Array.isArray(question?.Categories) ? question.Categories : [];
        const qAns = Array.isArray(question?.Answers) ? question.Answers : [];
        const qMatches = Array.isArray(question?.Matches) ? question.Matches : [];

        let parsed = rawAnswers;

        // Robust JSON unwrapping (handles single, double, or triple stringified JSON or nested { answers: ... })
        while (typeof parsed === 'string') {
            const trimmed = parsed.trim();
            if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
                try {
                    parsed = JSON.parse(trimmed);
                } catch {
                    break;
                }
            } else {
                break;
            }
        }

        while (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && parsed.answers !== undefined) {
            let inner = parsed.answers;
            while (typeof inner === 'string') {
                const trimmed = inner.trim();
                if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
                    try {
                        inner = JSON.parse(trimmed);
                    } catch {
                        break;
                    }
                } else {
                    break;
                }
            }
            parsed = inner;
        }

        const rawItems = Array.isArray(parsed) ? parsed : (parsed !== null && parsed !== undefined ? [parsed] : []);

        // -------------------------------------------------------------
        // Case 1: SelectView / MulitSelectView / QuestionnaireView (Types 1, 7, 8)
        // -------------------------------------------------------------
        if (typeId === 1 || typeId === 7 || typeId === 8 || (qAns.length > 0 && qFills.length === 0 && qCats.length === 0 && qMatches.length === 0)) {
            const selectedIds = new Set();
            rawItems.forEach(item => {
                if (typeof item === 'object' && item !== null) {
                    if (item.isChecked === true || item.isChecked === 1 || item.isChecked === 'true') {
                        if (item.Id !== undefined) selectedIds.add(Number(item.Id));
                        if (item.AnswerId !== undefined) selectedIds.add(Number(item.AnswerId));
                    } else if (item.Id !== undefined && item.isChecked === undefined) {
                        selectedIds.add(Number(item.Id));
                    } else if (item.AnswerId !== undefined && item.isChecked === undefined) {
                        selectedIds.add(Number(item.AnswerId));
                    }
                } else if (!isNaN(Number(item))) {
                    selectedIds.add(Number(item));
                }
            });

            return qAns.map((a, idx) => {
                const aId = Number(a.Id ?? a.id ?? 0);
                const isTrue = a.True === 1 || a.True === true || a.true === 1 || a.true === true;
                const isChecked = selectedIds.has(aId) || (selectedIds.size === 0 && Boolean(solve?.Mark >= question?.Mark && isTrue));
                return {
                    ...a,
                    Id: a.Id !== undefined ? a.Id : aId,
                    Name: a.Name || '',
                    True: isTrue ? 1 : 0,
                    Sort: a.Sort !== undefined ? a.Sort : idx,
                    isChecked: isChecked,
                    Value: a.Name || ''
                };
            });
        }

        // -------------------------------------------------------------
        // Case 2: Fill Questions (Types 3, 4, 5, 6, 13, 20, 32, 33, 34)
        // -------------------------------------------------------------
        if (qFills.length > 0 || typeId === 3 || typeId === 4 || typeId === 5 || typeId === 6 || typeId === 20 || typeId === 32 || typeId === 33 || typeId === 34) {
            const userMark = solve?.Mark !== undefined && solve?.Mark !== null ? Number(solve.Mark) : 0;
            const qMark = Number(question?.Mark ?? 0);
            const hasFullMark = Boolean((userMark > 0 && userMark >= qMark) || solve?.Score === 100 || solve?.IsPassed);

            // Reconstruct items if rawItems is empty or hasFullMark
            let itemsToProcess = rawItems.length > 0 ? rawItems : (qFills.length > 0 ? qFills : []);

            // Check if items array is 0-indexed (e.g. contains Sort: 0)
            const isZeroIndexed = rawItems.some(it => typeof it === 'object' && it !== null && it.Sort === 0);

            // Ensure categories CustomStyle.Answers exists for FillListView (Type 5) GetAnswerList() check
            qCats.forEach(cat => {
                if (!cat.CustomStyle) cat.CustomStyle = {};
                if (!Array.isArray(cat.CustomStyle.Answers) || cat.CustomStyle.Answers.length === 0) {
                    const catFills = qFills.filter(f => Number(f.CategoryId) === Number(cat.Id));
                    cat.CustomStyle.Answers = catFills.map((f, fIdx) => ({
                        Id: cat.Id,
                        Sort: f.Sort !== undefined ? Number(f.Sort) - 1 : fIdx,
                        Answer: String(f.AnswerId)
                    }));
                }
            });

            return itemsToProcess.map((item, idx) => {
                if (item === null || item === undefined) return null;

                // 1. Determine target sort
                let rawSort = (typeof item === 'object' && item !== null) ? item.Sort : undefined;
                let sortVal = rawSort !== undefined && rawSort !== null && !isNaN(Number(rawSort))
                    ? (isZeroIndexed ? Number(rawSort) + 1 : Number(rawSort))
                    : (idx + 1);
                if (sortVal === 0) sortVal = 1;

                // 2. Extract raw answer id and text
                let rawAns = (typeof item === 'object' && item !== null) 
                    ? (item.AnswerId !== undefined ? item.AnswerId : (item.Answer !== undefined ? item.Answer : (item.Value !== undefined ? item.Value : item.Id)))
                    : item;
                
                let ansId = 0;
                let ansText = '';
                if (typeof rawAns === 'object' && rawAns !== null) {
                    ansId = Number(rawAns.Id || rawAns.AnswerId || 0);
                    ansText = typeof rawAns.Name === 'string' ? rawAns.Name : (typeof rawAns.Value === 'string' ? rawAns.Value : (typeof rawAns.Answer === 'string' ? rawAns.Answer : ''));
                } else if (typeof rawAns === 'number') {
                    ansId = rawAns;
                } else if (typeof rawAns === 'string') {
                    if (!isNaN(Number(rawAns)) && rawAns.trim() !== '') {
                        ansId = Number(rawAns);
                    } else {
                        ansText = rawAns;
                        const foundByName = qAns.find(a => a.Name && (
                            a.Name.trim() === rawAns.trim() || 
                            a.Name.replace(/<[^>]*>/g, '').trim() === rawAns.replace(/<[^>]*>/g, '').trim()
                        ));
                        if (foundByName) {
                            ansId = Number(foundByName.Id);
                        }
                    }
                }

                if (!ansId && typeof item === 'object' && item?.Id && qAns.some(a => Number(a.Id) === Number(item.Id))) {
                    ansId = Number(item.Id);
                }

                // 3. Extract raw category id
                let rawCat = (typeof item === 'object' && item !== null) 
                    ? (item.CategoryId !== undefined ? item.CategoryId : (item.Category !== undefined ? item.Category : undefined))
                    : undefined;
                
                let catId = 0;
                if (typeof rawCat === 'object' && rawCat !== null) {
                    catId = Number(rawCat.Id || 0);
                } else if (rawCat !== undefined && !isNaN(Number(rawCat))) {
                    catId = Number(rawCat);
                } else if (typeof item === 'object' && item?.Id && qCats.some(c => Number(c.Id) === Number(item.Id))) {
                    catId = Number(item.Id);
                }

                // 4. Align with question.Fills
                let matchingFill = qFills[idx] || qFills.find(f => Number(f.Sort) === sortVal) || qFills.find(f => Number(f.AnswerId) === ansId) || qFills[0];

                let finalSort = matchingFill ? matchingFill.Sort : sortVal;
                let finalCategoryId = matchingFill ? matchingFill.CategoryId : (catId > 0 ? catId : (qCats[0]?.Id || 0));
                
                // If student got full mark or answered correctly, strictly align AnswerId to matchingFill.AnswerId
                let finalAnswerId = ansId;
                if (matchingFill && (hasFullMark || Number(matchingFill.AnswerId) === Number(ansId) || (ansText && qAns.find(a => Number(a.Id) === Number(matchingFill.AnswerId))?.Name === ansText))) {
                    finalAnswerId = matchingFill.AnswerId;
                    finalCategoryId = matchingFill.CategoryId;
                    finalSort = matchingFill.Sort;
                } else {
                    const foundInAnswers = qAns.find(a => Number(a.Id) === Number(ansId));
                    if (foundInAnswers) {
                        finalAnswerId = foundInAnswers.Id;
                    }
                }

                const foundCat = qCats.find(c => Number(c.Id) === Number(finalCategoryId)) || { Id: finalCategoryId, Name: '' };
                const foundAns = qAns.find(a => Number(a.Id) === Number(finalAnswerId));
                const answerName = foundAns?.Name || ansText || (typeof rawAns === 'string' ? rawAns : '');

                const normalizedAnswerObj = {
                    Id: finalAnswerId,
                    Sort: typeof finalSort === 'number' ? finalSort - 1 : 0,
                    AnswersSelectType: String(finalCategoryId),
                    Name: answerName,
                    ...foundAns
                };

                const isTrue = matchingFill 
                    ? (Number(matchingFill.AnswerId) === Number(finalAnswerId) && Number(matchingFill.CategoryId) === Number(finalCategoryId) && Number(matchingFill.Sort) === Number(finalSort))
                    : (typeof item === 'object' && item?.isTrue !== undefined ? Boolean(item.isTrue) : Boolean(hasFullMark));

                // Formulate the appropriate Answer field:
                // Types 5 (FillListView) and 6 (FillChooseView) use parseInt(answer.Answer) -> MUST BE String(finalAnswerId)
                // Types 4, 33, 34 (FillTextView) use answer.Answer as text string -> MUST BE answerName
                // Type 3 (FillView) uses answer.AnswerId
                const answerFieldValue = (typeId === 4 || typeId === 33 || typeId === 34) 
                    ? (answerName || String(finalAnswerId))
                    : String(finalAnswerId);

                return {
                    ...(typeof item === 'object' && item !== null ? item : {}),
                    Id: finalCategoryId,
                    CategoryId: finalCategoryId,
                    Category: foundCat,
                    AnswerId: finalAnswerId,
                    Answer: answerFieldValue,
                    AnswerObj: normalizedAnswerObj,
                    Value: answerName || String(finalAnswerId),
                    Sort: finalSort,
                    isTrue: isTrue
                };
            }).filter(Boolean);
        }

        // -------------------------------------------------------------
        // Case 3: Matches (Type 9)
        // -------------------------------------------------------------
        if (typeId === 9 || qMatches.length > 0) {
            return rawItems.map((item, idx) => {
                if (typeof item === 'object' && item !== null) {
                    return {
                        ...item,
                        Id: item.Id !== undefined ? item.Id : (qMatches[idx]?.Id || idx + 1),
                        Source: item.Source !== undefined ? item.Source : (qMatches[idx]?.Source || ''),
                        Target: item.Target !== undefined ? item.Target : (qMatches[idx]?.Target || '')
                    };
                }
                return {
                    Id: qMatches[idx]?.Id || idx + 1,
                    Source: qMatches[idx]?.Source || '',
                    Target: String(item)
                };
            });
        }

        // -------------------------------------------------------------
        // Case 4: Default generic adaptation
        // -------------------------------------------------------------
        return rawItems.map((item, idx) => {
            if (typeof item === 'object' && item !== null) {
                return {
                    ...item,
                    Sort: item.Sort !== undefined ? Number(item.Sort) : idx + 1
                };
            }
            return {
                Id: idx + 1,
                Sort: idx + 1,
                Value: String(item)
            };
        });
    };

    const marks = sls.map(s => {
        const qId = Number(s?.QuestionId ?? s?.Id ?? 0);
        const userMark = s?.Mark !== undefined && s?.Mark !== null ? Number(s.Mark) : 0;
        return {
            Id: qId,
            userMark: userMark,
            Score: s?.Score ?? 0,
            Star: s?.Star ?? 0,
            Time: Number(s?.Time ?? 0),
            IsPassed: Boolean(s?.IsPassed),
            IsTimesUp: Boolean(s?.IsTimesUp),
            TryNumber: Number(s?.TryNumber ?? 1)
        };
    });

    const current = sls.map(s => {
        const qId = Number(s?.QuestionId ?? s?.Id ?? 0);
        const matchingQuestion = qs.find(q => Number(q?.Id ?? 0) === qId);
        const rawAns = s?.answers !== undefined ? s.answers : s?.Answer;
        const adaptedAnswers = adaptSingleAnswers(rawAns, matchingQuestion, s);
        return {
            Id: matchingQuestion?.Id !== undefined ? matchingQuestion.Id : qId,
            answers: adaptedAnswers,
            mark: Number(s?.Mark ?? 0)
        };
    });

    const elapsedSeconds = marks.reduce((sum, m) => sum + (m.Time || 0), 0);

    return {
        exam: {
            marks,
            current
        },
        elapsedSeconds
    };
}
